import React, { useEffect } from 'react'

import { runtime } from '@deltachat-desktop/runtime-interface'
import { DialogBody, DialogContent, DialogWithHeader } from '../../Dialog'
import useTranslationFunction from '../../../hooks/useTranslationFunction'
import useDialog from '../../../hooks/dialog/useDialog'
import { LogDialog } from '../Log'
import SettingsIconButton from '../../Settings/SettingsIconButton'
import SettingsSeparator from '../../Settings/SettingsSeparator'

import styles from './styles.module.scss'

import type { DialogProps } from '../../../contexts/DialogContext'

const GITHUB_URL = 'https://github.com/experintellia/slothfulchat-web'
const DELTA_CHAT_URL = 'https://delta.chat'

export default function About({ onClose }: DialogProps) {
  const tx = useTranslationFunction()

  useEffect(() => {
    window.__aboutDialogOpened = true
    return () => {
      window.__aboutDialogOpened = false
    }
  }, [])

  const { openDialog } = useDialog()
  const viewLog = () => {
    openDialog(LogDialog)
    // Close the about dialog so that it doesn't interfere with sharing the log.
    onClose()
  }

  const runtimeInfo = runtime.getRuntimeInfo()
  const { VERSION } = runtimeInfo.buildInfo
  const slothfulConfig = (window as any).__slothfulConfig

  return (
    <DialogWithHeader
      width={400}
      title={tx('global_menu_help_about_desktop')}
      onClose={onClose}
      fullscreenOnMobile
    >
      <DialogBody>
        <DialogContent>
          <div className={styles.aboutContent}>
            <img
              className={styles.appIcon}
              src='./images/icon-512.png'
              alt='SlothfulChat'
            />
            <div className={styles.appVersion}>
              v{VERSION}
              {slothfulConfig?.commitHash && (
                <>
                  <br />
                  <small>
                    slothfulchat-web
                    {slothfulConfig.version ? ` v${slothfulConfig.version}` : ''}
                    {' '}({slothfulConfig.commitHash})
                    {slothfulConfig.commitMessage && (
                      <span
                        className={styles.commitMessageHint}
                        title={slothfulConfig.commitMessage}
                      >
                        {' '}ⓘ
                      </span>
                    )}
                  </small>
                </>
              )}
            </div>
            <p style={{ marginTop: 16, textAlign: 'left', fontSize: 13 }}>
              <b>slothfulchat-web</b> is an unofficial experiment. It runs Delta
              Chat's chatmail core — the same core behind the Delta Chat
              messenger — compiled to WebAssembly, and a fork of Delta Chat
              Desktop's browser edition web UI, running entirely in your
              browser. The port to WASM was done with an AI coding agent. Not
              affiliated with Delta Chat. A WS→TCP bridge is required to send
              and receive.
            </p>
          </div>
        </DialogContent>
        <SettingsSeparator />
        <SettingsIconButton
          icon='code-tags'
          isLink
          onClick={() => runtime.openLink(GITHUB_URL)}
          dataTestid='about-github'
        >
          {'SlothfulChat on GitHub'}
        </SettingsIconButton>
        <SettingsIconButton
          icon='sell'
          onClick={() =>
            runtime.openLink(new URL('changelog/', document.baseURI).href)
          }
          dataTestid='about-changelog'
        >
          {'Changelog'}
        </SettingsIconButton>
        <SettingsIconButton
          icon='chat_bubble'
          isLink
          onClick={() => runtime.openLink(DELTA_CHAT_URL)}
          dataTestid='about-delta-chat'
        >
          {'Delta Chat website'}
        </SettingsIconButton>
        <SettingsIconButton
          icon='info'
          onClick={() =>
            runtime.openLink(new URL('imprint.html', document.baseURI).href)
          }
          dataTestid='about-imprint'
        >
          {'Imprint'}
        </SettingsIconButton>
        <SettingsIconButton
          icon='visibility'
          onClick={() =>
            runtime.openLink(new URL('privacy.html', document.baseURI).href)
          }
          dataTestid='about-privacy'
        >
          {'Privacy policy'}
        </SettingsIconButton>
        <SettingsSeparator />
        <SettingsIconButton
          icon='question_mark'
          onClick={() => runtime.openHelpWindow()}
          dataTestid='about-help'
        >
          {tx('menu_help')}
        </SettingsIconButton>
        <SettingsIconButton
          icon='visibility'
          onClick={viewLog}
          dataTestid='about-view-log'
        >
          {tx('pref_view_log')}
        </SettingsIconButton>
      </DialogBody>
    </DialogWithHeader>
  )
}
